import { TeamMember, PayPeriod, LineItem } from '../types'

const firstNames = [
  'Sarah',
  'Michael',
  'Jennifer',
  'David',
  'Lisa',
  'Robert',
  'Amanda',
  'James',
  'Emily',
  'Christopher',
  'Jessica',
  'Daniel',
]
const lastNames = [
  'Johnson',
  'Williams',
  'Martinez',
  'Garcia',
  'Rodriguez',
  'Chen',
  'Patel',
  'Anderson',
  'Taylor',
  'Thomas',
  'Moore',
  'Jackson',
]

const statuses = [
  'Started',
  'In Process',
  'Validated & Locked',
  'Sent to LO for Validation',
  'Pending Revision',
  'Pending Level 2 Sign-off',
  'Pending Payment',
  'Paid by ACH',
  'Paid by Check',
  'Paid by Wire',
] as const

const paymentMethods = ['Wire', 'ACH', 'Check'] as const

const commissionModels = ['Flat Fee', 'Standard'] as const

const paymentTypes = [
  'Payment',
  'Payment',
  'Payment',
  'Payment',
  'Manager Override',
  'Manager Override',
  'Referral Payment',
  'Referral Payment',
  'Recruiting Bonus',
  'Expense',
]

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function randomChoice<T>(arr: readonly T[]): T {
  return arr[randomInt(0, arr.length - 1)]
}

const states = [
  'CA',
  'TX',
  'FL',
  'NY',
  'WA',
  'CO',
  'AZ',
  'NV',
  'OR',
  'GA',
  'NC',
  'IL',
]
const loanPurposes = [
  'Purchase',
  'Refinance',
  'Cash-Out Refinance',
  'Construction',
]
const compensationTypes = ['Borrower Paid', 'Lender Paid']
const lenders = [
  'Wells Fargo',
  'Chase',
  'Rocket Mortgage',
  'United Wholesale Mortgage',
  'loanDepot',
  'Caliber Home Loans',
  'PennyMac',
  'Guaranteed Rate',
  'Fairway Independent',
  'Newrez',
]

function generateLineItems(
  count: number,
  periodStart: Date,
  periodEnd: Date,
  commissionModel: 'Flat Fee' | 'Standard',
  loName: string,
): LineItem[] {
  return Array.from({ length: count }, (_, i) => {
    const paymentType = randomChoice(paymentTypes)
    let amount: number
    let loanAmount: number
    let extra: Partial<LineItem> = {}

    if (paymentType === 'Payment') {
      loanAmount = randomInt(200000, 800000)
      const gross = Math.round(loanAmount * (randomInt(80, 200) / 10000)) // 0.8% - 2% of loan
      const loSplit = randomInt(60, 90) // percent

      // Random closed date within the pay period
      const closedDateTs =
        periodStart.getTime() +
        Math.random() * (periodEnd.getTime() - periodStart.getTime())

      const lender = randomChoice(lenders)
      if (commissionModel === 'Flat Fee') {
        const flat = randomInt(500, 1200)
        const processing = randomInt(150, 450)
        const net = Math.max(0, gross - flat - processing)
        const payroll = Math.round((net * loSplit) / 100)
        amount = payroll
        extra = {
          closedDate: new Date(closedDateTs),
          propertyState: randomChoice(states),
          loanPurpose: randomChoice(loanPurposes),
          lender,
          compensationType: randomChoice(compensationTypes),
          loName,
          gross,
          flat,
          processing,
          net,
          loSplit,
          payroll,
        }
      } else {
        const corpFee = Math.round(gross * (randomInt(15, 25) / 100)) // 15-25% corp fee
        const net = Math.max(0, gross - corpFee)
        const lo = Math.round((net * loSplit) / 100)
        const payroll = lo
        const balance = net - lo
        amount = payroll
        extra = {
          closedDate: new Date(closedDateTs),
          propertyState: randomChoice(states),
          loanPurpose: randomChoice(loanPurposes),
          lender,
          compensationType: randomChoice(compensationTypes),
          loName,
          gross,
          corpFee,
          net,
          loSplit,
          lo,
          payroll,
          balance,
        }
      }
    } else if (paymentType === 'Manager Override') {
      loanAmount = randomInt(200000, 800000)
      const gross = Math.round(loanAmount * (randomInt(80, 200) / 10000))
      const flat = randomInt(500, 1200)
      const processing = randomInt(150, 450)
      const net = Math.max(0, gross - flat - processing)
      const managerSplit = randomInt(5, 15)
      const managerNet = Math.round((net * managerSplit) / 100)
      const closedDateTs =
        periodStart.getTime() +
        Math.random() * (periodEnd.getTime() - periodStart.getTime())
      amount = managerNet
      extra = {
        closedDate: new Date(closedDateTs),
        propertyState: randomChoice(states),
        loanPurpose: randomChoice(loanPurposes),
        compensationType: randomChoice(compensationTypes),
        loName,
        gross,
        flat,
        processing,
        net,
        managerSplit,
        managerNet,
      }
    } else if (paymentType === 'Referral Payment') {
      loanAmount = randomInt(200000, 600000)
      const gross = Math.round(loanAmount * (randomInt(80, 200) / 10000))
      const flat = randomInt(500, 1200)
      const processing = randomInt(150, 450)
      const net = Math.max(0, gross - flat - processing)
      // Referral payment is a slice of net (10-25%)
      const referralPay = Math.round((net * randomInt(10, 25)) / 100)
      const closedDateTs =
        periodStart.getTime() +
        Math.random() * (periodEnd.getTime() - periodStart.getTime())
      const loaFirsts = [
        'Alex',
        'Jordan',
        'Taylor',
        'Morgan',
        'Riley',
        'Casey',
        'Drew',
      ]
      const loaLasts = ['Kim', 'Brown', 'Davis', 'Lee', 'Wilson', 'Reed']
      amount = referralPay
      extra = {
        closedDate: new Date(closedDateTs),
        propertyState: randomChoice(states),
        loaName: `${randomChoice(loaFirsts)} ${randomChoice(loaLasts)}`,
        gross,
        flat,
        processing,
        net,
      }
    } else if (paymentType === 'Recruiting Bonus') {
      amount = randomInt(1000, 5000)
      loanAmount = randomInt(200000, 800000)
      const closedDateTs =
        periodStart.getTime() +
        Math.random() * (periodEnd.getTime() - periodStart.getTime())
      extra = {
        closedDate: new Date(closedDateTs),
        propertyState: randomChoice(states),
        loanPurpose: randomChoice(loanPurposes),
        loName,
      }
    } else if (paymentType === 'Expense') {
      amount = randomInt(50, 800)
      loanAmount = 0
      const categories = [
        'Marketing',
        'Travel',
        'Office Supplies',
        'Software',
        'Training',
        'Meals',
        'Conference',
      ]
      const details = [
        'Reimbursement for client lunch',
        'Monthly software subscription',
        'Mileage reimbursement',
        'Printed marketing materials',
        'Conference registration fee',
        'Hotel and travel expenses',
        'Office equipment purchase',
        'Continuing education course',
      ]
      const itemNames = [
        'Client Lunch',
        'CRM Subscription',
        'Mileage',
        'Flyers & Postcards',
        'Industry Conference',
        'Travel — Out of State',
        'Monitor / Equipment',
        'CE Course',
      ]
      const expenseDateTs =
        periodStart.getTime() +
        Math.random() * (periodEnd.getTime() - periodStart.getTime())
      extra = {
        itemName: randomChoice(itemNames),
        expenseCategory: randomChoice(categories),
        expenseDate: new Date(expenseDateTs),
        expenseDetail: randomChoice(details),
      }
    } else {
      amount = randomInt(500, 3000)
      loanAmount = 0
    }

    return {
      id: `line-${Math.random().toString(36).substr(2, 9)}`,
      opportunityId: `opp-${Math.random().toString(36).substr(2, 9)}`,
      opportunityName: `${randomInt(100, 999)} ${randomChoice(['Oak', 'Maple', 'Pine', 'Cedar', 'Elm'])} ${randomChoice(['St', 'Ave', 'Ln', 'Dr', 'Ct'])}`,
      paymentType,
      amount,
      loanAmount,
      ...extra,
    }
  })
}

function generatePayPeriods(
  count: number,
  commissionModel: 'Flat Fee' | 'Standard',
  loName: string,
): PayPeriod[] {
  const today = new Date()
  return Array.from({ length: count }, (_, i) => {
    const periodStart = new Date(today.getFullYear(), today.getMonth() - i, 1)
    const periodEnd = new Date(today.getFullYear(), today.getMonth() - i + 1, 0)
    const lineItems = generateLineItems(
      randomInt(8, 14),
      periodStart,
      periodEnd,
      commissionModel,
      loName,
    )

    return {
      id: `period-${Math.random().toString(36).substr(2, 9)}`,
      startDate: periodStart,
      endDate: periodEnd,
      units: lineItems.filter((li) => li.paymentType === 'Payment').length,
      commissionAmount: lineItems.reduce(
        (sum, li) =>
          li.paymentType === 'Expense' ? sum - li.amount : sum + li.amount,
        0,
      ),
      loanAmount: lineItems.reduce((sum, li) => sum + li.loanAmount, 0),
      lineItems,
    }
  })
}

export function generateMockTeamMembers(count: number = 12): TeamMember[] {
  return Array.from({ length: count }, (_, i) => {
    const firstName = randomChoice(firstNames)
    const lastName = randomChoice(lastNames)
    const fullName = `${firstName} ${lastName}`
    const commissionModel = randomChoice(commissionModels)
    const payPeriods = generatePayPeriods(
      randomInt(2, 3),
      commissionModel,
      fullName,
    )
    const totalPayrollAmount = payPeriods.reduce(
      (sum, pp) => sum + pp.commissionAmount,
      0,
    )
    const paymentItemCount = payPeriods.reduce((sum, pp) => sum + pp.units, 0)
    const referralPaymentItemCount = payPeriods.reduce(
      (sum, pp) =>
        sum +
        pp.lineItems.filter((li) => li.paymentType === 'Referral Payment')
          .length,
      0,
    )

    return {
      id: `member-${i}`,
      name: fullName,
      status: randomChoice(statuses),
      preferredPaymentMethod: randomChoice(paymentMethods),
      payPeriod: 'Dec 2024',
      totalPayrollAmount,
      paymentItemCount,
      referralPaymentItemCount,
      units: paymentItemCount,
      commission: totalPayrollAmount,
      volume: payPeriods.reduce((sum, pp) => sum + pp.loanAmount, 0),
      payPeriods,
      commissionModel,
      isExpanded: false,
    }
  })
}
